import { BrowserRouter, Routes, Route } from 'react-router-dom';

import App from './App';
import { AngularRouter, Page404 } from './pages';
import { MigrationGate } from './pages/MigrationGate';
import { Availability } from './pages/NUX';
import { CancelReasons } from './pages/CancelReasons';
import { Dashboard } from './pages/Dashboard';
import { Feedback } from './pages/Feedback';
import { Notifications } from './pages/Notifications';
import {
  ParentFeedbackAvailability,
  ParentFeedbackMain,
  ParentFeedbackMessage,
  ParentFeedbackSafety,
  ParentFeedbackSuccess,
} from './pages/ParentFeedback';
import { ParentProfile } from './pages/ParentProfile';
import { ParentReviews } from './pages/ParentReviews';
import { FinishBackgroundCheck } from './pages/FinishBackgroundCheck';
import { AboutMe as SitterRegistrationAboutMe } from './pages/SitterRegistration/AboutMe';
import { VideoUpload as SitterVideoUpload } from './pages/SitterRegistration/VideoUpload';
import { Confirm as SitterRegistrationConfirm } from './pages/SitterRegistration/Confirm';
import { Location as SitterRegistrationLocation } from './pages/SitterRegistration/Location';
import { SitterRegistrationPhoto } from './pages/SitterRegistration/Photo';
import { Rates as SitterRegistrationRates } from './pages/SitterRegistration/Rates';
import { WorksWith as SitterRegistrationWorksWith } from './pages/SitterRegistration/WorksWith';
import { SitterFriends } from './pages/SitterFriends';
import { SitterPayout } from './pages/SitterPayout';
import { SitterReviewReply } from './pages/SitterReviewReply';
import { SitterReviews } from './pages/SitterReviews';
import { SitterStats } from './pages/SitterStats';
import { SuccessGuide } from './pages/SuccessGuide';
import { YourFamilies } from './pages/YourFamilies';
import { BuyBackgroundCheck } from './pages/background-check/BuyBackgroundCheck';
import { BookingConfirmHours } from './pages/BookingConfirmHours';
import { BookingDetail } from './pages/BookingDetail';
import { BookingList } from './pages/BookingList';
import { ChildcareTransactions } from './pages/ChildcareTransactions';
import { Credits } from './pages/Credits';
import { JobBoard } from './pages/JobBoard';
import { JobDetail } from './pages/JobDetail';
import { Reimbursements } from './pages/Reimbursements';
import { Settings } from './pages/Settings';
import { SitterBookingCreate } from './pages/SitterBookingCreate';
import { SitterCalendar } from './pages/Calendar';
import { SitterProfile } from './pages/SitterProfile';
import { CreditCard } from './pages/CreditCard';
import { MessageThread } from './pages/MessageThread';
import { Messages } from './pages/Messages';
import { SitterPayment } from './pages/SitterPayment';
import { Transactions } from './pages/Transactions';

// MigrationGate wraps each route that has a brand-new React port. Default
// behavior forwards to the still-live Angular page (zero user-visible change);
// ?force=family / ?force=caregiver renders the React port and persists in a
// session cookie. Routes that previously had an Angular page but no React port
// yet just use AngularRouter directly.
export const Router = (): JSX.Element => (
  <BrowserRouter basename='/app'>
    <Routes>
      <Route path='/' element={<App />}>
        <Route index element={<AngularRouter path='' />} />
        <Route path='bookings'>
          <Route index element={<MigrationGate angularPath='bookings'><BookingList /></MigrationGate>} />
          <Route path=':id' element={<MigrationGate angularPath='bookings/:id'><BookingDetail /></MigrationGate>} />
          <Route path=':id/confirm-hours' element={<MigrationGate angularPath='bookings/:id/confirm-hours'><BookingConfirmHours /></MigrationGate>} />
          <Route path=':id/:detail' element={<AngularRouter path='bookings/:id/:detail' />} />
        </Route>
        <Route path='buy-background-check' element={<BuyBackgroundCheck />} />
        <Route path='finish-background-check' element={<FinishBackgroundCheck />} />
        <Route path='sitter-cancel-reasons' element={<MigrationGate angularPath='sitter-cancel-reasons'><CancelReasons /></MigrationGate>} />
        <Route path='caregiver'>
          <Route path='dashboard' element={<Dashboard />} />
          <Route path='feedback' element={<Feedback />} />
          <Route path='buy-background-check' element={<BuyBackgroundCheck />} />
        </Route>
        <Route path='job'>
          <Route path=':id' element={<MigrationGate angularPath='job/:id'><JobDetail /></MigrationGate>} />
        </Route>
        <Route path='job-board' element={<MigrationGate angularPath='job-board'><JobBoard /></MigrationGate>} />
        <Route path='parent'>
          <Route path='dashboard' element={<AngularRouter path='family/dashboard' />} />
          <Route path=':id' element={<MigrationGate angularPath='parent/:id'><ParentProfile /></MigrationGate>} />
          <Route path=':id/reviews' element={<MigrationGate angularPath='parent/:id/reviews'><ParentReviews /></MigrationGate>} />
          <Route path=':id/feedback' element={<MigrationGate angularPath='parent/:id/feedback'><ParentFeedbackMain /></MigrationGate>} />
          <Route path=':id/feedback/safety' element={<MigrationGate angularPath='parent/:id/feedback/safety'><ParentFeedbackSafety /></MigrationGate>} />
          <Route path=':id/feedback/message' element={<MigrationGate angularPath='parent/:id/feedback/message'><ParentFeedbackMessage /></MigrationGate>} />
          <Route path=':id/feedback/availability' element={<MigrationGate angularPath='parent/:id/feedback/availability'><ParentFeedbackAvailability /></MigrationGate>} />
          <Route path=':id/feedback/success' element={<MigrationGate angularPath='parent/:id/feedback/success'><ParentFeedbackSuccess /></MigrationGate>} />
        </Route>
        <Route path='sitter-calendar' element={<MigrationGate angularPath='sitter-calendar'><SitterCalendar /></MigrationGate>} />
        <Route path='sitter'>
          <Route path=':id' element={<MigrationGate angularPath='sitter/:id'><SitterProfile /></MigrationGate>} />
          <Route path=':id/calendar' element={<MigrationGate angularPath='sitter/:id/calendar'><SitterCalendar /></MigrationGate>} />
          <Route path=':id/friends' element={<MigrationGate angularPath='sitter/:id/friends'><SitterFriends /></MigrationGate>} />
          <Route path=':id/messages' element={<MigrationGate angularPath='sitter/:id/messages'><Messages /></MigrationGate>} />
          <Route path=':id/messages/:parentId' element={<MigrationGate angularPath='sitter/:id/messages/:parentId'><MessageThread /></MigrationGate>} />
          <Route path=':id/notifications' element={<MigrationGate angularPath='sitter/:id/notifications'><Notifications /></MigrationGate>} />
          <Route path=':id/recommend' element={<AngularRouter path='sitter/:id/recommend' />} />
          <Route path=':id/reviews' element={<MigrationGate angularPath='sitter/:id/reviews'><SitterReviews /></MigrationGate>} />
          <Route path=':id/reviews/:reviewId' element={<MigrationGate angularPath='sitter/:id/reviews/:reviewId'><SitterReviewReply /></MigrationGate>} />
          <Route path=':id/settings' element={<MigrationGate angularPath='sitter/:id/settings'><Settings /></MigrationGate>} />
          <Route path=':id/stats' element={<MigrationGate angularPath='sitter/:id/stats'><SitterStats /></MigrationGate>} />
          <Route path=':id/your-families' element={<MigrationGate angularPath='sitter/:id/your-families'><YourFamilies /></MigrationGate>} />
          <Route path='booking/create' element={<MigrationGate angularPath='sitter/booking/create'><SitterBookingCreate /></MigrationGate>} />
          <Route path='payment' element={<SitterPayment />} />
          <Route path='payout' element={<SitterPayout />} />
          <Route path='registration'>
            <Route path='about-me' element={<SitterRegistrationAboutMe />} />
            <Route path='availability' element={<Availability />} />
            <Route path='confirm' element={<SitterRegistrationConfirm />} />
            <Route path='location' element={<SitterRegistrationLocation />} />
            <Route path='photo' element={<SitterRegistrationPhoto />} />
            <Route path='rates' element={<SitterRegistrationRates />} />
            <Route path='works-with' element={<SitterRegistrationWorksWith />} />
          </Route>
          <Route path='success-guide' element={<SuccessGuide />} />
          <Route path='video' element={<SitterVideoUpload />} />
        </Route>
        <Route path='user'>
          <Route path=':id/childcare-transactions' element={<MigrationGate angularPath='user/:id/childcare-transactions'><ChildcareTransactions /></MigrationGate>} />
          <Route path=':id/credit-card' element={<MigrationGate angularPath='user/:id/credit-card'><CreditCard /></MigrationGate>} />
          <Route path=':id/credits' element={<Credits />} />
          <Route path=':id/reimbursements' element={<Reimbursements />} />
          <Route path=':id/transactions' element={<MigrationGate angularPath='user/:id/transactions'><Transactions /></MigrationGate>} />
        </Route>
        <Route path='credits' element={<Credits />} />
        <Route path='*' element={<Page404 />} />
      </Route>
    </Routes>
  </BrowserRouter>
);
